import React from "react";

import { Controller, useFormContext } from "react-hook-form";
import { TimeInput } from "semantic-ui-calendar-react";
import { Form, Label } from "semantic-ui-react";
import { getCurrentDateTime, getDatePart } from "../../helpers/datetime";
import useLogin from "@common/components/hooks/useLogin";

/**
 * TimeComponent renders a time component with validation.
 *
 * @typedef {object} TimeProps
 * @property {Function} control - Function to control the time field for form
 * @property {string} name - Name of the time field.
 * @property {string} [label=''] - Label of the time field.
 * @property {string} placeholder - Placeholder text of the time field.
 * @property {Object} [error=null] - Object containing validation error.
 * @property {boolean} [labelAbove=false] - Boolean to determine if the label is above or next to the time field.
 * @property {string} [timeFormat='HH:mm'] - The format of the time.
 * @property {string} [fieldStyle='flex-row-center'] - Style for Form.Field.
 * @property {string} [labelStyle=''] - Style for the label.
 * @property {string} [timeContainerStyle='w-100'] - Style for time field container.
 * @property {boolean} [disabled] - Boolean to disable the input.
 *
 * @component
 * @param {TimeProps} props - The component props.
 * @returns {JSX.Element} The rendered time input field with validation.
 */

export default function TimeComponent({
  control,
  name,
  dateName,
  label = "",
  placeholder = "",
  error = null,
  labelAbove = false,
  timeFormat = "HH:mm",
  fieldStyle = "flex-row-center",
  labelStyle,
  timeContainerStyle = "w-100",
  disabled = false,
}) {
  const { setValue, watch } = useFormContext();
  const [{ isInReadMode }] = useLogin();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Form.Field
          className={`${!labelAbove && "flex-between "} ${fieldStyle}`}
          error={!!error}
        >
          {label && (
            <label className={`${!labelAbove && "w-30 "} ${labelStyle}`}>
              {label}
            </label>
          )}
          <div className={timeContainerStyle}>
            <TimeInput
              icon={disabled ? false : "time"}
              value={value}
              onChange={(_, { value }) => {
                onChange(value);
                if (!watch(dateName)) {
                  setValue(dateName, getDatePart(getCurrentDateTime()));
                }
              }}
              dateFormat={timeFormat}
              closable
              placeholder={placeholder}
              disabled={disabled}
              readOnly={isInReadMode}
            />
            {error && (
              <Label pointing="above" prompt>
                {error?.message}
              </Label>
            )}
          </div>
        </Form.Field>
      )}
    />
  );
}
